import { useState, useCallback } from "react";
import { useTableSort } from "./useTableSort";

const EMPTY_CONFIRM = { open: false, title: "", message: "", onConfirm: null, mode: undefined };

/**
 * Shared list chrome: selection, search/sort/view, optional import panel, banner, confirm dialog.
 * Use initialViewMode "card" | "table" (or extend with e.g. "calendar" via setViewMode).
 *
 * @param {object} [config]
 * @param {{ key: string|null, direction: 'asc'|'desc' }} [config.initialSort]
 * @param {string} [config.initialViewMode]
 */
export function useEntityListChrome(config = {}) {
  const {
    initialSort = { key: null, direction: "asc" },
    initialViewMode = "table",
  } = config;

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { sortConfig, setSortConfig, handleSort } = useTableSort(initialSort);
  const [bannerError, setBannerError] = useState("");
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [confirmDialog, setConfirmDialog] = useState(EMPTY_CONFIRM);

  const closeConfirmDialog = useCallback(() => setConfirmDialog(EMPTY_CONFIRM), []);

  const handleSelectAll = useCallback((e, getFilteredRows) => {
    const filteredData = typeof getFilteredRows === "function" ? getFilteredRows() : [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (e.target.checked) filteredData.forEach((obj) => next.add(obj._id));
      else filteredData.forEach((obj) => next.delete(obj._id));
      return next;
    });
  }, []);

  const handleSelectOne = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  return {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  };
}
